import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { money } from '../../lib/money';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Badge,
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  PageContainer,
  PageHeader,
  Select,
  Spinner,
  Textarea,
  useToast,
} from '../../ui/components';
import { Icon } from '../../ui/icons';
import { useProperty } from '../../app/PropertyContext';
import { useT } from '../../i18n';
import { catalogApi } from '../catalog/catalogApi';
import { purchaseOrderPath, goodsReceiptPath } from '../../lib/slug';
import { purchaseOrdersApi, poStatusMeta } from '../purchase-orders/purchaseOrdersApi';
import { receivingApi, DISCREPANCY_REASONS } from './receivingApi';
import { makeAttachmentsApi } from '../attachments/attachmentsApi';
import { settingsApi } from '../settings/settingsApi';
import '../requisitions/requisitions.css';

const grnAttachmentsApi = (grnId) => makeAttachmentsApi(`/goods-receipts/${grnId}/attachments`);

const todayIso = () => new Date().toISOString().slice(0, 10);

export default function ReceiveGoodsPage() {
  const { id: routeRef } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();
  const t = useT();
  const { hasPermission, activePropertyId } = useProperty();
  const canRecord = hasPermission('receiving.record');

  // Two modes share this form: receiving against a PO (/purchase-orders/:id/receive)
  // and editing an existing draft receipt (/receiving/:id/edit).
  const isEditMode = location.pathname.endsWith('/edit');

  const [po, setPo] = useState(null);
  const [grn, setGrn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [units, setUnits] = useState([]);
  const [locations, setLocations] = useState([]);
  const [busy, setBusy] = useState(false);

  const [qty, setQty] = useState({});
  const [rej, setRej] = useState({});
  const [reason, setReason] = useState({});
  const [lineNote, setLineNote] = useState({});
  // Actual unit price on the supplier's delivery note/invoice, when it differs from the PO price.
  const [price, setPrice] = useState({});
  const [rejectedEarlier, setRejectedEarlier] = useState({});
  const [receivedDate, setReceivedDate] = useState(todayIso());
  const [deliveryNote, setDeliveryNote] = useState('');
  const [locationId, setLocationId] = useState('');
  const [headerNotes, setHeaderNotes] = useState('');

  // Per-line delivery photos, keyed by PO line id. Pending photos are local files uploaded when
  // the draft is saved/posted; existing photos are attachments already registered on the draft.
  const [requirePhotos, setRequirePhotos] = useState(false);
  const [pendingPhotos, setPendingPhotos] = useState({}); // { poLineId: [{ key, file, url }] }
  const [existingPhotos, setExistingPhotos] = useState({}); // { poLineId: [{ id, fileName, url }] }
  const photoInputs = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let poData;
      let draft = null;

      if (isEditMode) {
        draft = await receivingApi.get(routeRef);
        if (draft.status !== 'Draft') {
          setGrn(draft);
          setError(t('This goods receipt has been posted and can no longer be edited.'));
          setLoading(false);
          return;
        }
        setGrn(draft);
        poData = await purchaseOrdersApi.get(draft.purchaseOrderId);
      } else {
        poData = await purchaseOrdersApi.get(routeRef);
      }

      setPo(poData);
      setLocationId(draft?.locationId || poData.locationId || '');

      const seedQ = {};
      const seedR = {};
      const seedReason = {};
      const seedNote = {};

      const seedPrice = {};
      if (draft) {
        (draft.lines || []).forEach((gl) => {
          seedQ[gl.purchaseOrderLineId] = String(gl.receivedQuantity ?? 0);
          if (Number(gl.rejectedQuantity || 0) > 0) seedR[gl.purchaseOrderLineId] = String(gl.rejectedQuantity);
          if (gl.discrepancyReason && gl.discrepancyReason !== 'None') seedReason[gl.purchaseOrderLineId] = gl.discrepancyReason;
          if (gl.notes) seedNote[gl.purchaseOrderLineId] = gl.notes;
          if (gl.hasPriceVariance && gl.unitPrice) seedPrice[gl.purchaseOrderLineId] = String(gl.unitPrice.amount);
        });
        setReceivedDate((draft.receivedDate || new Date().toISOString()).slice(0, 10));
        setDeliveryNote(draft.deliveryNote || '');
        setHeaderNotes(draft.notes || '');
      } else {
        (poData.lines || []).forEach((l) => {
          const outstanding = Number(l.quantity) - Number(l.receivedQuantity || 0);
          seedQ[l.lineId] = outstanding > 0 ? String(outstanding) : '0';
        });
      }

      setQty(seedQ);
      setRej(seedR);
      setReason(seedReason);
      setLineNote(seedNote);
      setPrice(seedPrice);

      // Load photos already registered on the draft, keyed back to their PO line.
      setPendingPhotos({});
      if (draft) {
        try {
          const attApi = grnAttachmentsApi(draft.id);
          const atts = (await attApi.list()) || [];
          const poLineByGrnLine = {};
          (draft.lines || []).forEach((gl) => { poLineByGrnLine[gl.lineId] = gl.purchaseOrderLineId; });
          const photos = {};
          await Promise.all(
            atts
              .filter((a) => a.lineId && poLineByGrnLine[a.lineId])
              .map(async (a) => {
                let url = null;
                try { url = (await attApi.downloadUrl(a.id))?.url || null; } catch { /* thumbnail is optional */ }
                const poLineId = poLineByGrnLine[a.lineId];
                (photos[poLineId] = photos[poLineId] || []).push({ id: a.id, fileName: a.fileName, url });
              })
          );
          setExistingPhotos(photos);
        } catch {
          setExistingPhotos({});
        }
      } else {
        setExistingPhotos({});
      }

      // Sum quantities rejected on prior *posted* receipts per PO line, so the breakdown reconciles.
      // Drafts (including the one being edited) never count as "earlier".
      try {
        const res = await receivingApi.list({ purchaseOrderId: poData.id, pageSize: 100 });
        const detailed = await Promise.all((res.items || []).map((g) => receivingApi.get(g.id).catch(() => null)));
        const rejMap = {};
        detailed.filter(Boolean).forEach((g) => {
          if (g.status !== 'Posted') return;
          (g.lines || []).forEach((gl) => {
            const r = Number(gl.rejectedQuantity || 0);
            if (r > 0) rejMap[gl.purchaseOrderLineId] = (rejMap[gl.purchaseOrderLineId] || 0) + r;
          });
        });
        setRejectedEarlier(rejMap);
      } catch { setRejectedEarlier({}); }
    } catch (err) {
      setError(err?.message || (isEditMode ? t('Goods receipt not found.') : t('Purchase order not found.')));
    } finally {
      setLoading(false);
    }
  }, [routeRef, isEditMode, t]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    let active = true;
    catalogApi.listUnits().then((u) => { if (active) setUnits(u || []); }).catch(() => {});
    catalogApi.listLocations(activePropertyId).then((l) => { if (active) setLocations(l || []); }).catch(() => {});
    return () => { active = false; };
  }, [activePropertyId]);

  // Whether the effective receiving rules demand a photo on every received line.
  // Resolved against the order's property so it matches what the backend enforces.
  useEffect(() => {
    if (!po) return undefined;
    let active = true;
    settingsApi.getEffectiveReceiving(po.propertyId || activePropertyId)
      .then((r) => { if (active) setRequirePhotos(!!r?.requireLinePhotosOnReceive); })
      .catch(() => { if (active) setRequirePhotos(false); });
    return () => { active = false; };
  }, [po, activePropertyId]);

  const uomCode = (uid) => units.find((u) => u.id === uid)?.code;

  const lines = useMemo(() => po?.lines || [], [po]);

  const { totalReceiving, hasOver, anyQty, hasDiscrepancy } = useMemo(() => {
    let total = 0; let over = false; let any = false; let rejected = false; let reasoned = false;
    lines.forEach((l) => {
      const q = Number(qty[l.lineId] || 0);
      const r = Number(rej[l.lineId] || 0);
      if (q > 0) { total += q * Number(l.unitPrice?.amount || 0); }
      if (q > 0 || r > 0) any = true;
      if (r > 0) rejected = true;
      if (reason[l.lineId]) reasoned = true;
      const outstanding = Number(l.quantity) - Number(l.receivedQuantity || 0);
      if (q > outstanding) over = true;
    });
    return { totalReceiving: total, hasOver: over, anyQty: any, hasDiscrepancy: rejected || over || reasoned };
  }, [lines, qty, rej, reason]);

  const receivable = po && ['Approved', 'Sent', 'PartiallyReceived'].includes(po.status);

  const setLineQty = (lineId, value) => {
    if (value !== '' && Number(value) < 0) return;
    setQty((prev) => ({ ...prev, [lineId]: value }));
  };

  const setLineRej = (lineId, value) => {
    if (value !== '' && Number(value) < 0) return;
    setRej((prev) => ({ ...prev, [lineId]: value }));
  };

  const setLineReason = (lineId, value) => setReason((prev) => ({ ...prev, [lineId]: value }));
  const setLineNoteValue = (lineId, value) => setLineNote((prev) => ({ ...prev, [lineId]: value }));
  const setLinePrice = (lineId, value) => setPrice((prev) => ({ ...prev, [lineId]: value }));

  const photoCount = useCallback(
    (poLineId) => (pendingPhotos[poLineId]?.length || 0) + (existingPhotos[poLineId]?.length || 0),
    [pendingPhotos, existingPhotos]
  );

  const addPendingPhotos = (poLineId, fileList) => {
    const files = Array.from(fileList || []).filter((f) => f && f.type.startsWith('image/'));
    if (files.length === 0) return;
    setPendingPhotos((prev) => ({
      ...prev,
      [poLineId]: [
        ...(prev[poLineId] || []),
        ...files.map((file) => ({ key: `${Date.now()}-${Math.random()}`, file, url: URL.createObjectURL(file) })),
      ],
    }));
  };

  const removePendingPhoto = (poLineId, key) => {
    setPendingPhotos((prev) => {
      const kept = (prev[poLineId] || []).filter((p) => {
        if (p.key !== key) return true;
        URL.revokeObjectURL(p.url);
        return false;
      });
      return { ...prev, [poLineId]: kept };
    });
  };

  const removeExistingPhoto = async (poLineId, attachmentId) => {
    if (!grn) return;
    try {
      await grnAttachmentsApi(grn.id).remove(attachmentId);
      setExistingPhotos((prev) => ({
        ...prev,
        [poLineId]: (prev[poLineId] || []).filter((p) => p.id !== attachmentId),
      }));
    } catch (err) {
      toast.error(t('Could not remove photo'), { description: err?.message });
    }
  };

  // Lines that still need a photo before the receipt can post (when the rule is on).
  const missingPhotoLines = useMemo(() => {
    if (!requirePhotos) return [];
    return lines.filter((l) => {
      const q = Number(qty[l.lineId] || 0);
      const r = Number(rej[l.lineId] || 0);
      return (q > 0 || r > 0) && photoCount(l.lineId) === 0;
    });
  }, [requirePhotos, lines, qty, rej, photoCount]);

  // After the draft exists, push any locally-held photos to S3 and register them per line.
  const uploadPendingPhotos = async (saved) => {
    const entries = Object.entries(pendingPhotos).filter(([, files]) => (files || []).length > 0);
    if (entries.length === 0) return;
    const attApi = grnAttachmentsApi(saved.id);
    const grnLineByPoLine = {};
    (saved.lines || []).forEach((gl) => { grnLineByPoLine[gl.purchaseOrderLineId] = gl.lineId; });
    for (const [poLineId, files] of entries) {
      const grnLineId = grnLineByPoLine[poLineId];
      if (!grnLineId) continue; // line was dropped (no quantity entered)
      for (const p of files) {
        // eslint-disable-next-line no-await-in-loop
        await attApi.upload(p.file, { attachmentType: 'Photo', lineId: grnLineId });
      }
    }
  };

  const receiveAllOutstanding = () => {
    const next = {};
    lines.forEach((l) => {
      const outstanding = Number(l.quantity) - Number(l.receivedQuantity || 0);
      next[l.lineId] = outstanding > 0 ? String(outstanding) : '0';
    });
    setQty(next);
  };

  const buildPayload = () => {
    const payloadLines = lines
      .map((l) => ({
        purchaseOrderLineId: l.lineId,
        receivedQuantity: Number(qty[l.lineId] || 0),
        rejectedQuantity: Number(rej[l.lineId] || 0),
        discrepancyReason: reason[l.lineId] || null,
        notes: (lineNote[l.lineId] || '').trim() || null,
        // Delivery-note price override; null keeps the committed PO price.
        unitPrice: price[l.lineId] !== undefined && price[l.lineId] !== '' && Number(price[l.lineId]) >= 0
          ? Number(price[l.lineId])
          : null,
      }))
      .filter((l) => l.receivedQuantity > 0 || l.rejectedQuantity > 0);

    if (payloadLines.length === 0) {
      toast.error(t('Enter an accepted or rejected quantity on at least one line.'));
      return null;
    }

    return {
      purchaseOrderId: po.id,
      receivedDate: receivedDate ? new Date(receivedDate).toISOString() : null,
      locationId: locationId || null,
      deliveryNote: deliveryNote.trim() || null,
      notes: headerNotes.trim() || null,
      lines: payloadLines,
    };
  };

  // Persist the current entries without committing to stock/PO/budget.
  const saveDraft = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setBusy(true);
    let saved = null;
    try {
      saved = isEditMode
        ? await receivingApi.update(grn.id, payload)
        : await receivingApi.create({ ...payload, post: false });
      await uploadPendingPhotos(saved);
      toast.success(t('Draft saved'));
      navigate(goodsReceiptPath(saved));
    } catch (err) {
      if (saved) {
        // Draft persisted but a photo upload failed — continue from the draft's edit page.
        toast.error(t('Draft saved, but some photos failed to upload'), { description: err?.message });
        navigate(goodsReceiptPath(saved, '/edit'));
        return;
      }
      toast.error(t('Could not save draft'), { description: err?.message });
      setBusy(false);
    }
  };

  // Commit the receipt: posts stock, advances the PO and writes purchase history.
  // The draft is saved first so photos can be uploaded and linked to lines before posting.
  const postReceipt = async () => {
    const payload = buildPayload();
    if (!payload) return;
    if (missingPhotoLines.length > 0) {
      toast.error(t('Add a photo to every received line before posting.'));
      return;
    }
    setBusy(true);
    let saved = null;
    try {
      saved = isEditMode
        ? await receivingApi.update(grn.id, payload)
        : await receivingApi.create({ ...payload, post: false });
      await uploadPendingPhotos(saved);
      const posted = await receivingApi.post(saved.id);
      toast.success(t('Goods receipt {number} posted', { number: posted.number }));
      navigate(goodsReceiptPath(posted));
    } catch (err) {
      toast.error(t('Could not post receipt'), { description: err?.message });
      if (saved && !isEditMode) {
        // The draft exists — continue from its edit page instead of re-creating it.
        navigate(goodsReceiptPath(saved, '/edit'));
        return;
      }
      if (saved) load(); // resync draft + photos so already-uploaded photos aren't re-sent
      setBusy(false);
    }
  };

  if (loading) {
    return <PageContainer size="full"><div style={{ display: 'grid', placeItems: 'center', padding: 'var(--space-16)' }}><Spinner size={26} /></div></PageContainer>;
  }
  if (error || !po) {
    return (
      <PageContainer size="full">
        <EmptyState
          icon={<Icon.CircleAlert size={28} />}
          title={isEditMode ? t("Can't edit this receipt") : t("Couldn't load purchase order")}
          description={error || t('Not found.')}
          action={
            grn
              ? <Button variant="secondary" onClick={() => navigate(goodsReceiptPath(grn))}>{t('View receipt')}</Button>
              : <Button variant="secondary" onClick={() => navigate('/receiving')}>{t('Back to receiving')}</Button>
          }
        />
      </PageContainer>
    );
  }

  const statusMeta = poStatusMeta(po.status);

  const renderLine = (l) => {
    const outstanding = Number(l.quantity) - Number(l.receivedQuantity || 0);
    const accepted = Number(qty[l.lineId] || 0);
    const rejected = Number(rej[l.lineId] || 0);
    const over = accepted > outstanding;
    const flagged = rejected > 0 || !!reason[l.lineId];
    const needsPhoto = requirePhotos && (accepted > 0 || rejected > 0);
    const missingPhoto = needsPhoto && photoCount(l.lineId) === 0;
    const uom = uomCode(l.uomId) ? ` ${uomCode(l.uomId)}` : '';
    const meta = [
      l.sku,
      l.unitPrice ? money(l.unitPrice) : null,
      [l.costCenter, l.glAccountCode].filter(Boolean).join(' / ') || null,
    ].filter(Boolean).join(' · ');

    return (
      <div key={l.lineId} className={`rcv-line${flagged ? ' is-flagged' : ''}`}>
        <div className="rcv-line__top">
          <div style={{ minWidth: 0 }}>
            <div className="rcv-line__name">{l.description}</div>
            {meta && <div className="rcv-line__meta">{meta}</div>}
          </div>
          <div className="rcv-line__stats">
            <div className="rcv-stat" title={t('Total quantity on this purchase order line')}>
              <span className="rcv-stat__label">{t('Ordered')}</span>
              <span className="rcv-stat__value">{l.quantity}{uom}</span>
            </div>
            <div className="rcv-stat" title={t('Quantity accepted into stock on earlier deliveries')}>
              <span className="rcv-stat__label">{t('Accepted earlier')}</span>
              <span className="rcv-stat__value">{l.receivedQuantity || 0}{uom}</span>
            </div>
            {Number(rejectedEarlier[l.lineId] || 0) > 0 && (
              <div className="rcv-stat" title={t("Quantity rejected on earlier deliveries — still owed by the supplier, so it stays in 'Still due' until re-delivered")}>
                <span className="rcv-stat__label">{t('Rejected earlier')}</span>
                <span className="rcv-stat__value" style={{ color: 'var(--color-danger-600, #b42318)' }}>{rejectedEarlier[l.lineId]}{uom}</span>
              </div>
            )}
            <div className="rcv-stat" title={t('Quantity still owed by the supplier (ordered − accepted). Includes anything previously rejected, since that must be re-delivered.')}>
              <span className="rcv-stat__label">{t('Still due')}</span>
              <span className="rcv-stat__value">{outstanding}{uom}</span>
            </div>
          </div>
        </div>

        <div className="rcv-line__fields">
          <div className="rcv-field rcv-field--qty">
            <span className="rcv-field__label rcv-field__label--accept">{t('Accept')}</span>
            <Input
              type="number"
              min="0"
              step="any"
              value={qty[l.lineId] ?? ''}
              onChange={(e) => setLineQty(l.lineId, e.target.value)}
              wrapClassName={over ? 'is-over' : ''}
              style={{ textAlign: 'right' }}
            />
          </div>
          <div className="rcv-field rcv-field--qty">
            <span className="rcv-field__label rcv-field__label--reject">{t('Reject')}</span>
            <Input
              type="number"
              min="0"
              step="any"
              value={rej[l.lineId] ?? ''}
              onChange={(e) => setLineRej(l.lineId, e.target.value)}
              placeholder="0"
              style={{ textAlign: 'right' }}
            />
          </div>
          <div className="rcv-field rcv-field--qty" title={t('Actual unit price on the delivery note / invoice. Leave blank to keep the PO price.')}>
            <span className="rcv-field__label">{t('Price on note')}</span>
            <Input
              type="number"
              min="0"
              step="any"
              value={price[l.lineId] ?? ''}
              onChange={(e) => setLinePrice(l.lineId, e.target.value)}
              placeholder={l.unitPrice ? String(l.unitPrice.amount) : '0'}
              style={{ textAlign: 'right' }}
            />
          </div>
          {price[l.lineId] !== undefined && price[l.lineId] !== ''
            && Number(price[l.lineId]) !== Number(l.unitPrice?.amount || 0) && (
            <div className="rcv-field" style={{ alignSelf: 'center' }}>
              <Badge tone="warning">{t('Price variance')}</Badge>
            </div>
          )}
          {over && (
            <div className="rcv-field" style={{ alignSelf: 'center' }}>
              <Badge tone="warning">{t('Over-receipt')}</Badge>
            </div>
          )}
          {flagged && (
            <>
              <div className="rcv-field rcv-field--reason">
                <span className="rcv-field__label">{t('Reason for rejection')}</span>
                <Select
                  value={reason[l.lineId] || ''}
                  onChange={(v) => setLineReason(l.lineId, v)}
                  ariaLabel={t('Discrepancy reason')}
                  placeholder={t('Select a reason…')}
                  options={[{ value: '', label: t('Select a reason…') }, ...DISCREPANCY_REASONS.map((r) => ({ ...r, label: t(r.label) }))]}
                />
              </div>
              <div className="rcv-field rcv-field--note">
                <span className="rcv-field__label">{t('Note (optional)')}</span>
                <Input
                  value={lineNote[l.lineId] ?? ''}
                  onChange={(e) => setLineNoteValue(l.lineId, e.target.value)}
                  placeholder={t('e.g. 2 cartons crushed in transit')}
                />
              </div>
            </>
          )}
        </div>

        <div className={`rcv-photos${missingPhoto ? ' is-missing' : ''}`}>
          <span className="rcv-field__label">
            {t('Photos')}
            {needsPhoto && <span className="rcv-photos__req"> — {t('required to post')}</span>}
          </span>
          <div className="rcv-photos__row">
            {(existingPhotos[l.lineId] || []).map((p) => (
              <div key={p.id} className="rcv-photo" title={p.fileName}>
                {p.url
                  ? <img src={p.url} alt={p.fileName} />
                  : <span className="rcv-photo__fallback"><Icon.Image size={18} /></span>}
                {canRecord && (
                  <button
                    type="button"
                    className="rcv-photo__remove"
                    onClick={() => removeExistingPhoto(l.lineId, p.id)}
                    aria-label={t('Remove photo')}
                  >
                    <Icon.X size={11} />
                  </button>
                )}
              </div>
            ))}
            {(pendingPhotos[l.lineId] || []).map((p) => (
              <div key={p.key} className="rcv-photo is-pending" title={p.file.name}>
                <img src={p.url} alt={p.file.name} />
                <button
                  type="button"
                  className="rcv-photo__remove"
                  onClick={() => removePendingPhoto(l.lineId, p.key)}
                  aria-label={t('Remove photo')}
                >
                  <Icon.X size={11} />
                </button>
              </div>
            ))}
            <button
              type="button"
              className="rcv-photo__add"
              onClick={() => photoInputs.current[l.lineId]?.click()}
              disabled={!canRecord}
            >
              <Icon.Camera size={15} />
              <span>{t('Add photo')}</span>
            </button>
            <input
              ref={(el) => { photoInputs.current[l.lineId] = el; }}
              type="file"
              accept="image/*"
              multiple
              style={{ display: 'none' }}
              onChange={(e) => { addPendingPhotos(l.lineId, e.target.files); e.target.value = ''; }}
            />
          </div>
          {missingPhoto && (
            <div className="rcv-photos__hint">{t('Take or upload a photo of the delivered goods for this line.')}</div>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageContainer size="full">
      <Button
        variant="ghost"
        size="sm"
        leadingIcon={<Icon.ArrowLeft size={15} />}
        onClick={() => navigate(isEditMode ? goodsReceiptPath(grn) : purchaseOrderPath(po))}
        style={{ alignSelf: 'flex-start', marginBottom: 'var(--space-3)' }}
      >
        {isEditMode ? t('Back to draft') : po.number}
      </Button>

      <PageHeader
        title={`${isEditMode ? t('Edit draft') : t('Receive goods')} — ${po.number}`}
        description={
          <span>
            {isEditMode && <><Badge tone="neutral" dot>{t('Draft')}</Badge> · </>}
            {po.supplierName || t('No supplier')} · <Badge tone={statusMeta.tone} dot>{t(statusMeta.label)}</Badge>
          </span>
        }
        icon={<Icon.ArrowDownCircle size={22} />}
      />

      {!receivable && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t('Goods can only be received against an approved order. This order is {status}.', { status: t(statusMeta.label).toLowerCase() })}</Banner>
        </div>
      )}
      {hasOver && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t('One or more lines receive more than the outstanding quantity. This is allowed but will be flagged as an over-receipt.')}</Banner>
        </div>
      )}
      {hasDiscrepancy && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="warning">{t('This delivery has discrepancies. Rejected goods stay outstanding on the order, and the buyer and requester will be notified to follow up with the supplier.')}</Banner>
        </div>
      )}
      {requirePhotos && anyQty && missingPhotoLines.length > 0 && (
        <div style={{ marginBottom: 'var(--space-4)' }}>
          <Banner tone="info">{t('Photo evidence is required: add a photo to every line with an accepted or rejected quantity before posting. {count} line(s) still need a photo.', { count: missingPhotoLines.length })}</Banner>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        <Card>
          <Card.Body>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-4)' }}>
              <Input label={t('Received date')} type="date" value={receivedDate} onChange={(e) => setReceivedDate(e.target.value)} />
              <div className="ps-field">
                <span className="ps-field__label">{t('Receive into location')}</span>
                <Select
                  value={locationId}
                  onChange={setLocationId}
                  ariaLabel={t('Receive into location')}
                  placeholder={t('— Select location —')}
                  options={[{ value: '', label: t('— Select location —') }, ...locations.map((l) => ({ value: l.id, label: l.name }))]}
                  searchable
                />
              </div>
              <Input label={t('Delivery / packing note')} value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} placeholder={t('Supplier delivery note #')} />
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <div>
              <Card.Title>{t('Lines')}</Card.Title>
              <div className="pp-cell-muted" style={{ fontSize: 'var(--text-sm)', marginTop: 2 }}>
                {t('Enter how much you')} <strong>{t('accept')}</strong> {t('into stock. If a delivery is short, damaged or wrong, enter the')} <strong>{t('reject')}</strong> {t('quantity and a reason — rejected goods stay outstanding on the order.')}
              </div>
            </div>
            <Button variant="ghost" size="sm" leadingIcon={<Icon.Check size={14} />} onClick={receiveAllOutstanding}>{t('Receive all outstanding')}</Button>
          </Card.Header>
          <Card.Body>
            <div className="rcv-lines">
              {lines.map(renderLine)}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Header><Card.Title>{t('Notes')}</Card.Title></Card.Header>
          <Card.Body>
            <Textarea rows={2} value={headerNotes} onChange={(e) => setHeaderNotes(e.target.value)} placeholder={t('Condition, discrepancies, who delivered…')} />
          </Card.Body>
        </Card>

        <div className="req-form__actions" style={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="pp-cell-muted">{t('Receiving value:')} <strong>{money({ currency: po.currency, amount: totalReceiving })}</strong></div>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button variant="ghost" onClick={() => navigate(isEditMode ? goodsReceiptPath(grn) : purchaseOrderPath(po))} disabled={busy}>{t('Cancel')}</Button>
            <Button variant="secondary" loading={busy} disabled={!receivable || !canRecord || !anyQty} leadingIcon={<Icon.FileText size={15} />} onClick={saveDraft}>
              {t('Save draft')}
            </Button>
            <Button
              variant="primary"
              loading={busy}
              disabled={!receivable || !canRecord || !anyQty || missingPhotoLines.length > 0}
              leadingIcon={<Icon.ArrowDownCircle size={15} />}
              onClick={postReceipt}
            >
              {t('Post receipt')}
            </Button>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
